import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import Sidebar from "@/components/sidebar";
import { PageHeaderLayout } from "@/components/layouts/PageHeaderLayout";
import TicketCard from "@/components/ticket-card";
import EditTicketInlineModal from "@/components/edit-ticket-inline-modal";
import { Button } from "@/components/ui/button";
import { HeaderActionGroup, HeaderSearchInput } from "@/components/layouts/header-slots";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Filter } from "lucide-react";
import type { Ticket } from "@mangelapp/shared";
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@/hooks/use-permissions';
import { useAlert } from '@/hooks/use-alert';

export default function Tickets() {
  const { t } = useTranslation(['pages.tickets', 'common']);
  const { canCreateTickets } = usePermissions();
  const { alert } = useAlert();
  const [searchTerm, setSearchTerm] = useState("");
  const [isNewTicketModalOpen, setIsNewTicketModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [isTicketDialogOpen, setIsTicketDialogOpen] = useState(false);
  const [editingTicketId, setEditingTicketId] = useState<string | null>(null);

  // Debug-Log für Status-Änderungen
  useEffect(() => {
    console.log('🔥 isTicketDialogOpen Status geändert:', isTicketDialogOpen);
  }, [isTicketDialogOpen]);

  // Global event listener for dialog state
  useEffect(() => {
    const handleDialogOpen = () => {
      console.log('🔥 Dialog ÖFFNEN Event empfangen - Filter/Suche werden deaktiviert');
      setIsTicketDialogOpen(true);
    };
    const handleDialogClose = () => {
      console.log('🔥 Dialog SCHLIESSEN Event empfangen - Filter/Suche werden reaktiviert');
      setIsTicketDialogOpen(false);
    };

    window.addEventListener('ticket-dialog-open', handleDialogOpen);
    window.addEventListener('ticket-dialog-close', handleDialogClose);

    return () => {
      window.removeEventListener('ticket-dialog-open', handleDialogOpen);
      window.removeEventListener('ticket-dialog-close', handleDialogClose);
    };
  }, []);

  const { data: tickets = [], isLoading } = useQuery<Ticket[]>({
    queryKey: ["/api/tickets"],
  });

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (activeTab === "all") return matchesSearch;
    // Use statusSemanticType for filtering
    return matchesSearch && (ticket as any).statusSemanticType === activeTab;
  });

  const openTickets = tickets.filter(t => (t as any).statusSemanticType === 'open').length;
  const inProgressTickets = tickets.filter(t => (t as any).statusSemanticType === 'in_progress').length;
  const completedTickets = tickets.filter(t => (t as any).statusSemanticType === 'completed').length;

  if (isLoading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center ml-12 sm:ml-14 md:ml-16 lg:ml-14 xl:ml-16 2xl:ml-20">
          <div className="text-lg">{t('pages.tickets:loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />
      
      <div className="flex-1 flex flex-col overflow-hidden ml-12 sm:ml-14 md:ml-16 lg:ml-14 xl:ml-16 2xl:ml-20">
        <PageHeaderLayout
          pageTitle={t('pages.tickets:title')}
          pageSubtitle={t('pages.tickets:header.openCount', { count: openTickets })}
          searchComponent={
            <HeaderSearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder={t('pages.tickets:searchPlaceholder')}
              disabled={isTicketDialogOpen}
              inputClassName={
                isTicketDialogOpen
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed opacity-50"
                  : ""
              }
            />
          }
          tabs={
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="all">
                {t('pages.tickets:tabs.all', { count: tickets.length })}
              </TabsTrigger>
              <TabsTrigger value="open">
                {t('pages.tickets:tabs.open', { count: openTickets })}
              </TabsTrigger>
              <TabsTrigger value="in_progress">
                {t('pages.tickets:tabs.inProgress', { count: inProgressTickets })}
              </TabsTrigger>
              <TabsTrigger value="completed">
                {t('pages.tickets:tabs.completed', { count: completedTickets })}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          }
          actionButtons={
            <HeaderActionGroup>
              {/* Filter */}
              <Button 
                variant="outline" 
                disabled={isTicketDialogOpen}
                className={`h-7 px-2.5 text-xs font-medium ${
                  isTicketDialogOpen 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50 hover:bg-gray-100 hover:text-gray-400' 
                    : ''
                }`}
              >
                <Filter className={`h-3 w-3 ${
                  isTicketDialogOpen ? 'text-gray-300' : ''
                }`} />
                {t('pages.tickets:filter')}
              </Button>
              
              {/* New Ticket Button */}
              <Button 
                variant="outline" 
                onClick={async () => {
                  // Check if user has permission to create tickets
                  if (!canCreateTickets()) {
                    await alert({
                      message: t('common:accessDenied.message', 'You don\'t have permission to create tickets'),
                      title: t('common:accessDenied.title', 'Access Denied'),
                    });
                    return;
                  }
                  setIsNewTicketModalOpen(true);
                }} 
                className="h-7 px-2.5 text-xs font-medium"
              >
                <Plus className="h-3 w-3 mr-1" />
                {t('pages.tickets:newButton')}
              </Button>
            </HeaderActionGroup>
          }
        >
          {/* Main Content */}
          <div className="flex-1 overflow-auto p-6 bg-slate-50">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsContent value={activeTab} className="mt-6">
              {filteredTickets.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-slate-500 text-lg">
                    {activeTab === "all" 
                      ? t('pages.tickets:empty.noneFound') 
                      : activeTab === 'open'
                        ? t('pages.tickets:empty.noneOpen')
                        : activeTab === 'in_progress'
                          ? t('pages.tickets:empty.noneInProgress')
                          : t('pages.tickets:empty.noneCompleted')}
                  </div>
                  <Button 
                    className="mt-4" 
                    onClick={async () => {
                      // Check if user has permission to create tickets
                      if (!canCreateTickets()) {
                        await alert({
                          message: t('common:accessDenied.message', 'You don\'t have permission to create tickets'),
                          title: t('common:accessDenied.title', 'Access Denied'),
                        });
                        return;
                      }
                      setIsNewTicketModalOpen(true);
                    }}
                  >
                    {t('pages.tickets:empty.createFirst')}
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {filteredTickets.map((ticket) => (
                    <TicketCard 
                      key={ticket.id} 
                      ticket={ticket}
                      onEditOpen={() => {
                        setEditingTicketId(ticket.id);
                        setIsTicketDialogOpen(true);
                        console.log('🔥 DIREKT: Dialog geöffnet für Ticket:', ticket.id);
                      }}
                      onEditClose={() => {
                        setEditingTicketId(null);
                        setIsTicketDialogOpen(false);
                        console.log('🔥 DIREKT: Dialog geschlossen');
                      }}
                    />
                  ))}
                </div>
              )}
              </TabsContent>
            </Tabs>
          </div>
        </PageHeaderLayout>
      </div>

      {/* Modals */}
      <EditTicketInlineModal
        isOpen={isNewTicketModalOpen}
        onClose={() => setIsNewTicketModalOpen(false)}
        ticket={null}
        projectId={""}
      />
    </div>
  );
}
